import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';
import { CloudinaryPicture, WIDTH_LADDERS } from 'components/rich';

import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';
import tracker from '../lib/tracker';
import SETTINGS from '../settings';

// Evergreen landing page for the Parabo Press iPhone app. Its only job is App Store installs.
// Layout follows "iPhone App — Landing (desktop)" / "(mobile)" in design/website.pen.
// See docs/app-page.md.

const SHOP_URL = '/shop';
const COLORFUL_URL = '/colorful-square-prints';

const APP_STORE_URL = new URL(SETTINGS.APP_STORE_URL);
APP_STORE_URL.searchParams.set('utm_source', 'website');
APP_STORE_URL.searchParams.set('utm_medium', 'landing');
APP_STORE_URL.searchParams.set('utm_campaign', 'iphone_app');

// Shared with /colorful-square-prints: both encode the plain SETTINGS.APP_STORE_URL. Fork the file
// before either page changes its QR destination.
const QR_SRC = '/static/img/colorful-square-prints-app-qr.svg';

// App screenshots on the CDN (parabo-product-images images/screens/iphone-app/).
const screen = (name) => `screens/iphone-app/${name}`;
// Unsplash photos prepared for the photo book guide, see docs/unsplash-assets.md.
const photo = (name) => `landing/photo-book-guide/${name}`;

// Prints drawn on a stage. Geometry is [x, y, width] in design px, rotated around the top left
// corner like the design canvas (positive = counterclockwise). Converted to percentages of the
// stage so the composition scales with its column.
const HERO_STAGE = { desk: [620, 600], phone: [350, 400] };
const HERO_PRINTS = [
  {
    imageKey: photo('dog'),
    color: '#FFFFFF',
    rotate: 7,
    desk: [8, 96, 232],
    phone: [0, 70, 140],
    alt: 'Square Print of a dog',
  },
  {
    imageKey: photo('beach-selfie'),
    color: '#FFFFFF',
    rotate: -6,
    desk: [392, 28, 212],
    phone: [222, 24, 124],
    alt: 'Square Print of friends taking a selfie on a beach',
  },
  {
    imageKey: photo('kids'),
    color: '#EEE0AC',
    rotate: -4,
    desk: [380, 346, 220],
    phone: [214, 236, 132],
    alt: 'Square Print of two children with a yellow border',
  },
];
const HERO_PHONE = { desk: [178, 12, 270], phone: [83, 10, 184] };

const CLOSING_STAGE = { desk: [520, 200], phone: [330, 140] };
const CLOSING_PRINTS = [
  {
    imageKey: photo('dog'),
    color: '#D5B0AD',
    rotate: 8,
    desk: [40, 30, 150],
    phone: [20, 22, 104],
  },
  {
    imageKey: photo('coast'),
    color: '#FFFFFF',
    rotate: 0,
    desk: [182, 0, 168],
    phone: [113, 0, 116],
  },
  {
    imageKey: photo('kids'),
    color: '#C1CDB9',
    rotate: -6,
    desk: [340, 26, 150],
    phone: [222, 18, 104],
  },
];

// Range shown in the app's Store tab. Thumbnails reuse catalog images.
const RANGE = [
  {
    name: 'Prints',
    text: 'Classic, Glossy and Square Prints',
    image: { prefix: 'products/', imageKey: 'square-prints/1' },
  },
  { name: 'Photo books', text: 'Hardcover and Softcover', image: { imageKey: 'categories/books' } },
  { name: 'Cards', text: 'Holiday and greeting cards', image: { imageKey: 'categories/cards' } },
  {
    name: 'Wall art',
    text: 'Art, Engineer and Framed Prints',
    image: { imageKey: 'categories/wallart' },
  },
];

// Claims and sources: docs/app-page.md.
const BENEFITS = [
  {
    id: 'colorful',
    iosOnly: true,
    title: 'Colorful Square Prints',
    text: 'The classic white border, now in a shade that fits each photo. One tap colors the whole set. Same price, same paper.',
    link: { href: COLORFUL_URL, label: 'See Colorful Square Prints' },
    tone: '#C2D4DF',
    screen: {
      imageKey: screen('prints-border-color'),
      alt: 'The Background Color screen in the Parabo Press app, with border colors suggested from the photo',
    },
  },
  {
    id: 'wall',
    iosOnly: true,
    title: 'See it on your wall before you order',
    text: 'Point your camera at the wall. Wall art is shown at actual size, so you can pick the right one.',
    tone: '#E9E2D6',
  },
  {
    id: 'books',
    title: 'Every page is laid out for you',
    text: 'Pick your photos and the app fills the pages. Change any layout or add a caption.',
    tone: '#FBF7F0',
    screen: {
      imageKey: screen('books-editor-spreads'),
      alt: 'Spreads of a Softcover Photo Book laid out in the Parabo Press app',
    },
  },
  {
    id: 'cards',
    title: 'Cards with your own photos',
    text: 'Pick a design, add a photo and a note. Holiday and greeting cards in a few taps.',
    tone: '#F3E3DE',
    screen: {
      imageKey: screen('cards-editor'),
      alt: 'A birth announcement Photo Greeting Card in the Parabo Press app',
    },
  },
];

const COUPON_STEPS = [
  'Open your Bag and tap Apple Pay.',
  'Next to Coupon Code, tap Add and type your code.',
  'Check the new total. Your code shows as its own line above it. Then confirm with the side button.',
];

const trackCtaClick = (cta, location) => () => {
  tracker.onCustomEvent('app_page_cta_click', { cta, location });
};

const LOCATION = PropTypes.oneOf(['hero', 'benefits', 'closing']);

const percent = (value, total) => `${(value / total) * 100}%`;

const stageVars = ([x, y, width], [stageWidth, stageHeight], prefix) => ({
  [`--${prefix}-x`]: percent(x, stageWidth),
  [`--${prefix}-y`]: percent(y, stageHeight),
  [`--${prefix}-w`]: percent(width, stageWidth),
});

const placement = (item, stage) => ({
  '--rotate': `${-(item.rotate ?? 0)}deg`,
  ...stageVars(item.desk, stage.desk, 'desk'),
  ...stageVars(item.phone, stage.phone, 'phone'),
});

const stageRatios = (stage) => ({
  '--desk-ratio': `${stage.desk[0]} / ${stage.desk[1]}`,
  '--phone-ratio': `${stage.phone[0]} / ${stage.phone[1]}`,
});

// A Square Print: the padding is the printed border.
const Print = ({ print, stage, sizes, eager }) => (
  <div className="ipa-print" style={{ backgroundColor: print.color, ...placement(print, stage) }}>
    <div className="ipa-print-photo">
      <CloudinaryPicture
        imageKey={print.imageKey}
        widths={WIDTH_LADDERS.landing}
        sizes={sizes}
        alt={print.alt ?? ''}
        loading={eager ? undefined : 'lazy'}
      />
    </div>
  </div>
);

Print.propTypes = {
  print: PropTypes.object.isRequired,
  stage: PropTypes.object.isRequired,
  sizes: PropTypes.string.isRequired,
  eager: PropTypes.bool,
};

// iPhone frame around a screenshot or a drawn screen.
const Phone = ({ className, style, children }) => (
  <div className={classNames('ipa-phone', className)} style={style}>
    <div className="ipa-phone-screen">{children}</div>
  </div>
);

Phone.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
};

const Screenshot = ({ imageKey, alt, sizes, priority }) => (
  <CloudinaryPicture
    imageKey={imageKey}
    widths={WIDTH_LADDERS.screens}
    sizes={sizes}
    alt={alt}
    priority={priority}
    loading={priority ? undefined : 'lazy'}
  />
);

Screenshot.propTypes = {
  imageKey: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  sizes: PropTypes.string.isRequired,
  priority: PropTypes.bool,
};

const AppStoreBadge = ({ location }) => (
  <Anchor
    href={APP_STORE_URL.href}
    className="ipa-badge ipa-cta-phone"
    targetBlank
    onClick={trackCtaClick('app_store', location)}
  >
    <img
      src="/static/img/app-store-badge.svg"
      alt="Download Parabo Press on the App Store"
      width="162"
      height="54"
    />
  </Anchor>
);

AppStoreBadge.propTypes = { location: LOCATION.isRequired };

// Desktop handoff to the phone. The code encodes the plain App Store URL (no UTM) so it stays
// scannable, the tile links to the tracked URL for keyboard and screen reader users.
const QrHandoff = ({ location }) => (
  <Anchor
    href={APP_STORE_URL.href}
    className="ipa-qr ipa-cta-desk"
    targetBlank
    onClick={trackCtaClick('app_store', location)}
  >
    <span className="ipa-qr-code">
      <img
        src={QR_SRC}
        alt="QR code for the Parabo Press app on the App Store"
        width="66"
        height="66"
      />
    </span>
    <span className="ipa-qr-label">
      <span className="ipa-qr-title">Get the iPhone app</span>
      <span className="ipa-qr-text">
        Scan with your iPhone camera
        <br />
        to open the App Store
      </span>
    </span>
  </Anchor>
);

QrHandoff.propTypes = { location: LOCATION.isRequired };

// Desktop: QR code. Below lg: the App Store badge.
const AppCta = ({ location }) => (
  <div className="ipa-cta">
    <QrHandoff location={location} />
    <AppStoreBadge location={location} />
  </div>
);

AppCta.propTypes = { location: LOCATION.isRequired };

const Eyebrow = ({ children }) => <p className="ipa-eyebrow">{children}</p>;

Eyebrow.propTypes = { children: PropTypes.node.isRequired };

const Hero = () => (
  <section className="ipa-hero">
    <Container className="ipa-hero-grid">
      <div className="ipa-hero-copy">
        <p className="ipa-hero-eyebrow">
          <span className="ipa-dot" aria-hidden="true" />
          THE PARABO PRESS APP FOR iPHONE
        </p>
        <h1 className="ipa-h1">Your camera roll, on paper</h1>
        <p className="ipa-lead">
          Prints, photo books, cards and wall art, made right on your iPhone. Pick photos from your
          camera roll, check every print and order in a few taps.
        </p>
      </div>

      <div className="ipa-stage ipa-hero-stage" style={stageRatios(HERO_STAGE)}>
        {HERO_PRINTS.map((print) => (
          <Print
            key={print.imageKey}
            print={print}
            stage={HERO_STAGE}
            sizes="(max-width: 575px) 40vw, (max-width: 991px) 210px, 240px"
            eager
          />
        ))}
        <Phone className="ipa-hero-phone" style={placement(HERO_PHONE, HERO_STAGE)}>
          <Screenshot
            imageKey={screen('prints-editor')}
            alt="The Parabo Press app editing a set of Square Prints"
            sizes="(max-width: 575px) 53vw, (max-width: 991px) 280px, 260px"
            priority
          />
        </Phone>
      </div>

      <div className="ipa-hero-actions">
        <AppCta location="hero" />
        <p className="ipa-fallback">
          No iPhone?{' '}
          <Link href={SHOP_URL} className="ipa-link" onClick={trackCtaClick('shop', 'hero')}>
            Order on the web
            <PdpIcon name="arrowRight" size={16} />
          </Link>
        </p>
      </div>
    </Container>
  </section>
);

const Range = () => (
  <section className="ipa-range">
    <Container className="ipa-range-grid">
      <div className="ipa-range-title">
        <Eyebrow>IN THE APP</Eyebrow>
        <h2 className="ipa-range-h2">Everything you print with us, in one app</h2>
      </div>
      <ul className="ipa-range-tiles">
        {RANGE.map((item) => (
          <li key={item.name} className="ipa-range-tile">
            <span className="ipa-range-image">
              <CloudinaryPicture {...item.image} type="small" alt="" loading="lazy" />
            </span>
            <span className="ipa-range-text">
              <strong>{item.name}</strong>
              <span>{item.text}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="ipa-range-note">
        Also calendars, photo ropes and clip lights to hang your prints.
      </p>
    </Container>
  </section>
);

const Corner = ({ position }) => (
  <span className={`ipa-wall-corner ipa-wall-corner--${position}`} />
);

Corner.propTypes = { position: PropTypes.oneOf(['tl', 'tr', 'bl', 'br']).isRequired };

// Drawn copy of the app's View on Wall camera screen, not a capture.
const WallScreen = () => (
  <div className="ipa-wall" aria-hidden="true">
    <span className="ipa-wall-close">Close</span>
    <div className="ipa-wall-frame">
      <div className="ipa-wall-mat">
        <div className="ipa-wall-photo">
          <CloudinaryPicture
            imageKey={screen('wall-print-photo')}
            widths={WIDTH_LADDERS.screens}
            sizes="120px"
            alt=""
            loading="lazy"
          />
        </div>
      </div>
    </div>
    {['tl', 'tr', 'bl', 'br'].map((position) => (
      <Corner key={position} position={position} />
    ))}
    <span className="ipa-wall-hint">
      Drag to move.
      <br />
      Shown at actual size.
    </span>
    <span className="ipa-wall-floor" />
  </div>
);

const Benefit = ({ benefit }) => (
  <li className={`ipa-benefit ipa-benefit--${benefit.id}`}>
    <div className="ipa-benefit-visual" style={{ backgroundColor: benefit.tone }}>
      <Phone className="ipa-benefit-phone">
        {benefit.screen ? (
          <Screenshot imageKey={benefit.screen.imageKey} alt={benefit.screen.alt} sizes="234px" />
        ) : (
          <>
            <WallScreen />
            <span className="sr-only">
              The View on Wall screen in the Parabo Press app: a framed print shown on the wall
              through the camera
            </span>
          </>
        )}
      </Phone>
    </div>
    <div className="ipa-benefit-text">
      {benefit.iosOnly && (
        <p className="ipa-tag">
          <span className="ipa-dot" aria-hidden="true" />
          ONLY IN THE iOS APP
        </p>
      )}
      <h3 className="ipa-h3">{benefit.title}</h3>
      <p className="ipa-benefit-body">{benefit.text}</p>
      {benefit.link && (
        <Link
          href={benefit.link.href}
          className="ipa-link"
          onClick={trackCtaClick('colorful', 'benefits')}
        >
          {benefit.link.label}
          <PdpIcon name="arrowRight" size={16} />
        </Link>
      )}
    </div>
  </li>
);

Benefit.propTypes = { benefit: PropTypes.object.isRequired };

const Benefits = () => (
  <section className="ipa-section ipa-benefits">
    <Container>
      <div className="ipa-benefits-head">
        <Eyebrow>WHY THE APP</Eyebrow>
        <h2 className="ipa-h2">Made for the photos on your phone</h2>
      </div>
      <ul className="ipa-benefits-grid">
        {BENEFITS.map((benefit) => (
          <Benefit key={benefit.id} benefit={benefit} />
        ))}
      </ul>
    </Container>
  </section>
);

// Drawn copy of the iOS Apple Pay sheet with the Coupon Code row highlighted. Illustrative
// amounts, not a price quote.
const ApplePaySheet = () => (
  <div className="ipa-pay" aria-hidden="true">
    <div className="ipa-pay-top">
      <span className="ipa-pay-close">
        <PdpIcon name="x" size={20} />
      </span>
      <span className="ipa-pay-merchant">Pay Parabo</span>
      <span className="ipa-pay-amount">$25.00</span>
    </div>
    <div className="ipa-pay-row">
      <span className="ipa-pay-icon ipa-pay-icon--card">
        <PdpIcon name="creditCard" size={16} />
      </span>
      <span className="ipa-pay-text">
        <span className="ipa-pay-label">Debit Mastercard</span>
        <span className="ipa-pay-value">Pay $25.00</span>
      </span>
    </div>
    <div className="ipa-pay-row">
      <span className="ipa-pay-icon ipa-pay-icon--shipping">
        <PdpIcon name="package" size={16} />
      </span>
      <span className="ipa-pay-text">
        <span className="ipa-pay-label">Shipping Method</span>
        <span className="ipa-pay-value">Standard</span>
      </span>
      <PdpIcon name="chevronRight" size={18} className="ipa-pay-chevron" />
    </div>
    <div className="ipa-pay-coupon">
      <span>Coupon Code</span>
      <span className="ipa-pay-add">Add</span>
    </div>
    <div className="ipa-pay-summary">
      <span className="ipa-pay-line">
        <span>Square Prints</span>
        <span>$15.00</span>
      </span>
      <span className="ipa-pay-line">
        <span>Shipping</span>
        <span>$10.00</span>
      </span>
      <span className="ipa-pay-divider" />
      <span className="ipa-pay-line ipa-pay-line--total">
        <span>Total</span>
        <span>$25.00</span>
      </span>
    </div>
    <div className="ipa-pay-confirm">
      <PdpIcon name="smartphone" size={18} />
      Confirm with Side Button
    </div>
  </div>
);

const CouponCode = () => (
  <section className="ipa-section ipa-coupon">
    <Container className="ipa-coupon-grid">
      <div className="ipa-coupon-copy">
        <Eyebrow>HAVE A CODE?</Eyebrow>
        <h2 className="ipa-h2">Add your code in the Apple Pay sheet</h2>
        <p className="ipa-coupon-lead">
          The Bag has no code field. You add the code in the last step, right before you pay.
        </p>
        <ol className="ipa-steps">
          {COUPON_STEPS.map((step, index) => (
            <li key={step} className="ipa-step">
              <span className="ipa-step-number" aria-hidden="true">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
      <ApplePaySheet />
    </Container>
  </section>
);

const Closing = () => (
  <section className="ipa-section ipa-closing">
    <Container className="ipa-closing-inner">
      <div className="ipa-stage ipa-closing-stage" style={stageRatios(CLOSING_STAGE)}>
        {CLOSING_PRINTS.map((print) => (
          <Print
            key={print.imageKey}
            print={print}
            stage={CLOSING_STAGE}
            sizes="(max-width: 991px) 35vw, 170px"
          />
        ))}
      </div>
      <div className="ipa-closing-copy">
        <h2 className="ipa-h2">Your next prints are already on your phone</h2>
        <p className="ipa-closing-lead">Get the Parabo Press app and print them today.</p>
      </div>
      <AppCta location="closing" />
    </Container>
  </section>
);

const AppPage = () => (
  <>
    <Head
      fullTitle="Parabo Press App for iPhone: Photo Prints & Books"
      description="Print photos from your iPhone with the Parabo Press app. Prints, photo books, cards and wall art, made from your camera roll and ordered in a few taps."
    />

    <Layout>
      <div className="lnd ipa">
        <Hero />
        <Range />
        <Benefits />
        <CouponCode />
        <Closing />
      </div>
    </Layout>
  </>
);

export default AppPage;
